import { useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Alert,
  BodyText,
  Button,
  Checkbox,
  Tabs,
  Dialog,
  Input,
  PageHeader,
  Select,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-entities';
import {
  RefreshCw as RefreshIcon,
  Plus as AddIcon,
  Pencil as EditIcon,
  Trash2 as DeleteIcon,
} from 'lucide-react';
import {
  useSecuredParties,
  useCreateSecuredParty,
  useUpdateSecuredParty,
  useDeleteSecuredParty,
  useDebtors,
  useCreateDebtor,
  useUpdateDebtor,
  useDeleteDebtor,
  useJurisdictions,
} from '../../hooks';
import type {
  CreateDebtorRequest,
  CreateSecuredPartyRequest,
  UpdateDebtorRequest,
  UpdateSecuredPartyRequest,
} from '../../hooks/useUccEntities';
import { formatDate } from '../../utils/display-format';
import { UccTable, UccTenantScope } from './ucc-display';

type SecuredParty = components['schemas']['SecuredParty'];
type Debtor = components['schemas']['Debtor'];
type EntityTabState = 'secured-parties' | 'debtors';

/** A list row: one secured party or one debtor, keyed by the id the spec names for each. */
interface EntityRowState {
  id: string;
  legalName: string;
  kind: string;
  mailingAddress: components['schemas']['MailingAddress'];
  createdAt: string;
  source: SecuredParty | Debtor;
}

const TAB_ITEMS = [
  { id: 'secured-parties', label: 'Secured Parties' },
  { id: 'debtors', label: 'Debtors' },
];

const TAB_NOUN: Record<EntityTabState, { singular: string; plural: string }> = {
  'secured-parties': { singular: 'Secured Party', plural: 'secured parties' },
  debtors: { singular: 'Debtor', plural: 'debtors' },
};

export interface EntityFormData {
  legalName: string;
  organizationType: string;
  jurisdictionOfOrganization: string;
  organizationId: string;
  isIndividual: boolean;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export const EMPTY_ENTITY_FORM: EntityFormData = {
  legalName: '',
  organizationType: '',
  jurisdictionOfOrganization: '',
  organizationId: '',
  isIndividual: false,
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
};

function addressOf(form: EntityFormData): components['schemas']['MailingAddress'] {
  return {
    street: form.street.trim(),
    city: form.city.trim(),
    state: form.state.trim().toUpperCase(),
    zipCode: form.zipCode.trim(),
    country: form.country.trim() || 'US',
  };
}

/**
 * POST /secured-parties body per schema-registry/ucc-entities.openapi.yaml:
 * `legalName`, `organizationType` and `jurisdictionOfOrganization` are
 * required, and the address carries `zipCode`. The page posted `name` and
 * `postalCode`, which the spec does not declare, so every create failed
 * validation.
 */
export function buildSecuredPartyRequest(form: EntityFormData, tenantId: string): CreateSecuredPartyRequest {
  return {
    tenantId,
    legalName: form.legalName.trim(),
    organizationType: form.organizationType.trim(),
    jurisdictionOfOrganization: form.jurisdictionOfOrganization,
    organizationId: form.organizationId.trim() || null,
    mailingAddress: addressOf(form),
  };
}

/** POST /debtors body: as a secured party, plus the required `isIndividual`; organization fields are optional. */
export function buildDebtorRequest(form: EntityFormData, tenantId: string): CreateDebtorRequest {
  return {
    tenantId,
    legalName: form.legalName.trim(),
    isIndividual: form.isIndividual,
    organizationType: form.isIndividual ? null : form.organizationType.trim() || null,
    jurisdictionOfOrganization: form.isIndividual ? null : form.jurisdictionOfOrganization || null,
    organizationId: form.organizationId.trim() || null,
    mailingAddress: addressOf(form),
  };
}

function formFromRow(row: EntityRowState): EntityFormData {
  const src = row.source;
  const isIndividual = 'isIndividual' in src ? src.isIndividual : false;
  return {
    legalName: src.legalName,
    organizationType: src.organizationType ?? '',
    jurisdictionOfOrganization: src.jurisdictionOfOrganization ?? '',
    organizationId: src.organizationId ?? '',
    isIndividual,
    street: src.mailingAddress.street,
    city: src.mailingAddress.city,
    state: src.mailingAddress.state,
    zipCode: src.mailingAddress.zipCode,
    country: src.mailingAddress.country,
  };
}

function toRows(tab: EntityTabState, securedParties: SecuredParty[], debtors: Debtor[]): EntityRowState[] {
  if (tab === 'secured-parties') {
    return securedParties.map((sp) => ({
      id: sp.securedPartyId,
      legalName: sp.legalName,
      kind: sp.organizationType,
      mailingAddress: sp.mailingAddress,
      createdAt: sp.createdAt,
      source: sp,
    }));
  }
  return debtors.map((d) => ({
    id: d.debtorId,
    legalName: d.legalName,
    kind: d.isIndividual ? 'Individual' : d.organizationType ?? 'Organization',
    mailingAddress: d.mailingAddress,
    createdAt: d.createdAt,
    source: d,
  }));
}

function formIsComplete(tab: EntityTabState, form: EntityFormData): boolean {
  const addressComplete = Boolean(form.street.trim() && form.city.trim() && form.state.trim() && form.zipCode.trim());
  const organizationComplete = Boolean(form.organizationType.trim() && form.jurisdictionOfOrganization);
  const needsOrganization = tab === 'secured-parties' || !form.isIndividual;
  return Boolean(form.legalName.trim()) && addressComplete && (!needsOrganization || organizationComplete);
}

export function UCCEntitiesPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const userId = useAuthStore(s => s.user?.id);
  const [activeTab, setActiveTab] = useState<EntityTabState>('secured-parties');
  const [dialogMode, setDialogMode] = useState<{ mode: 'create' } | { mode: 'edit'; row: EntityRowState } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<EntityRowState | null>(null);
  const [entityForm, setEntityForm] = useState<EntityFormData>(EMPTY_ENTITY_FORM);

  const securedParties = useSecuredParties({ tenantId });
  const debtors = useDebtors({ tenantId });
  const jurisdictions = useJurisdictions();

  const createSP = useCreateSecuredParty();
  const updateSP = useUpdateSecuredParty();
  const deleteSP = useDeleteSecuredParty();
  const createDebtor = useCreateDebtor();
  const updateDebtor = useUpdateDebtor();
  const deleteDebtor = useDeleteDebtor();

  const isSecuredTab = activeTab === 'secured-parties';
  const list = isSecuredTab ? securedParties : debtors;
  const rows = toRows(activeTab, securedParties.data?.data ?? [], debtors.data?.data ?? []);
  const noun = TAB_NOUN[activeTab];

  const saveMutation = dialogMode?.mode === 'edit'
    ? (isSecuredTab ? updateSP : updateDebtor)
    : (isSecuredTab ? createSP : createDebtor);
  const deleteMutation = isSecuredTab ? deleteSP : deleteDebtor;

  const jurisdictionOptions = (jurisdictions.data?.data ?? []).map((j) => ({
    value: j.stateCode,
    label: `${j.stateName} (${j.stateCode})`,
  }));

  const openCreate = () => {
    saveMutation.reset();
    setEntityForm(EMPTY_ENTITY_FORM);
    setDialogMode({ mode: 'create' });
  };

  const openEdit = (row: EntityRowState) => {
    setEntityForm(formFromRow(row));
    setDialogMode({ mode: 'edit', row });
  };

  const closeDialog = () => {
    setDialogMode(null);
    createSP.reset();
    updateSP.reset();
    createDebtor.reset();
    updateDebtor.reset();
  };

  const handleSave = async () => {
    if (!dialogMode) return;
    try {
      if (dialogMode.mode === 'create') {
        if (isSecuredTab) await createSP.mutateAsync(buildSecuredPartyRequest(entityForm, tenantId));
        else await createDebtor.mutateAsync(buildDebtorRequest(entityForm, tenantId));
      } else {
        const { tenantId: _omitSp, ...spFields } = buildSecuredPartyRequest(entityForm, tenantId);
        const { tenantId: _omitDebtor, ...debtorFields } = buildDebtorRequest(entityForm, tenantId);
        if (isSecuredTab) {
          const body: UpdateSecuredPartyRequest = { ...spFields, changedBy: userId };
          await updateSP.mutateAsync({ securedPartyId: dialogMode.row.id, body });
        } else {
          const body: UpdateDebtorRequest = { ...debtorFields, changedBy: userId };
          await updateDebtor.mutateAsync({ debtorId: dialogMode.row.id, body });
        }
      }
      setDialogMode(null);
      setEntityForm(EMPTY_ENTITY_FORM);
    } catch {
      // The dialog renders the mutation error.
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch {
      // The confirmation dialog renders deleteMutation.error.
    }
  };

  // Built each render: the row actions close over the active tab's mutations.
  const columns: ColumnDef<EntityRowState, unknown>[] = [
    {
      accessorKey: 'legalName',
      header: 'Legal Name',
      cell: ({ row }) => <span className="text-sm font-medium text-primary">{row.original.legalName}</span>,
    },
    { accessorKey: 'kind', header: 'Type' },
    {
      id: 'address',
      header: 'Address',
      cell: ({ row }) => (
        <span className="text-sm text-secondary">
          {row.original.mailingAddress.city}, {row.original.mailingAddress.state}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-1 justify-end">
          <Button
            variant="tertiary"
            size="sm"
            className="h-11 w-11 justify-center"
            aria-label={`Edit ${row.original.legalName}`}
            onClick={() => openEdit(row.original)}
          >
            <EditIcon size={16} aria-hidden="true" />
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            className="h-11 w-11 justify-center"
            aria-label={`Delete ${row.original.legalName}`}
            onClick={() => { deleteMutation.reset(); setPendingDelete(row.original); }}
          >
            <DeleteIcon size={16} aria-hidden="true" />
          </Button>
        </div>
      ),
    },
  ];

  const needsOrganization = isSecuredTab || !entityForm.isIndividual;
  const dialogTitle = dialogMode?.mode === 'edit' ? `Edit ${noun.singular}` : `Create ${noun.singular}`;

  return (
    <div>
      <PageHeader
        description="Secured party and debtor management"
        actions={
          <>
            <Button variant="secondary" onClick={() => list.refetch()} disabled={list.isFetching || !tenantId}>
              <RefreshIcon className={`mr-2 ${list.isFetching ? 'animate-spin' : ''}`} size={16} />
              Refresh
            </Button>
            <Button variant="primary" onClick={openCreate} disabled={!tenantId}>
              <AddIcon className="mr-2" size={16} />
              Add {noun.singular}
            </Button>
          </>
        }
      />

      <UccTenantScope tenantId={tenantId} subject="lists" />

      {tenantId && (
        <Tabs
          tabs={TAB_ITEMS}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as EntityTabState)}
          aria-label="Entity type"
          panelClassName="mt-4"
        >
          <UccTable<EntityRowState>
            columns={columns}
            rows={rows}
            isLoading={list.isLoading}
            error={list.error}
            errorTitle={`Could not load ${noun.plural}`}
            onRetry={() => list.refetch()}
            empty={{
              title: `No ${noun.plural} for this tenant yet`,
              description: `Use Add ${noun.singular} to create one. UCC filings name a secured party and a debtor from these lists.`,
            }}
            getRowId={(row) => row.id}
          />
        </Tabs>
      )}

      <Dialog
        open={dialogMode !== null}
        onOpenChange={(open) => { if (!open) closeDialog(); }}
        title={dialogTitle}
        size="md"
        footer={
          <>
            <Button variant="tertiary" onClick={closeDialog} disabled={saveMutation.isPending}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending || !formIsComplete(activeTab, entityForm)}
            >
              {saveMutation.isPending ? 'Saving...' : dialogMode?.mode === 'edit' ? 'Save Changes' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {saveMutation.error && (
            <Alert type="error" title={`The ${noun.singular.toLowerCase()} could not be saved`}>
              {saveMutation.error.message}
            </Alert>
          )}
          <Input
            label="Legal Name"
            placeholder="Full legal name"
            value={entityForm.legalName}
            onChange={(e) => setEntityForm(prev => ({ ...prev, legalName: e.target.value }))}
            required
          />
          {!isSecuredTab && (
            <Checkbox
              label="This debtor is an individual"
              helperText="Organization type and jurisdiction of organization apply to organizations only."
              checked={entityForm.isIndividual}
              onChange={(checked) => setEntityForm(prev => ({ ...prev, isIndividual: checked }))}
            />
          )}
          {needsOrganization && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Organization Type"
                placeholder="e.g. corporation, LLC, partnership"
                value={entityForm.organizationType}
                onChange={(e) => setEntityForm(prev => ({ ...prev, organizationType: e.target.value }))}
                required
              />
              <Select
                label="Jurisdiction of Organization"
                options={jurisdictionOptions}
                placeholder={jurisdictions.error ? 'Jurisdictions could not be read' : 'Select a state'}
                value={entityForm.jurisdictionOfOrganization}
                onChange={(e) => setEntityForm(prev => ({ ...prev, jurisdictionOfOrganization: e.target.value }))}
                required
              />
            </div>
          )}
          <Input
            label="Organization ID"
            placeholder="State registration number (optional)"
            value={entityForm.organizationId}
            onChange={(e) => setEntityForm(prev => ({ ...prev, organizationId: e.target.value }))}
          />
          <Input
            label="Street Address"
            placeholder="123 Main St"
            value={entityForm.street}
            onChange={(e) => setEntityForm(prev => ({ ...prev, street: e.target.value }))}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="City"
              value={entityForm.city}
              onChange={(e) => setEntityForm(prev => ({ ...prev, city: e.target.value }))}
              required
            />
            <Input
              label="State"
              value={entityForm.state}
              onChange={(e) => setEntityForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
              maxLength={2}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="ZIP Code"
              value={entityForm.zipCode}
              onChange={(e) => setEntityForm(prev => ({ ...prev, zipCode: e.target.value }))}
              required
            />
            <Input
              label="Country"
              value={entityForm.country}
              onChange={(e) => setEntityForm(prev => ({ ...prev, country: e.target.value }))}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title={`Delete ${noun.singular}`}
        size="sm"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setPendingDelete(null)} disabled={deleteMutation.isPending}>Cancel</Button>
            <Button variant="primary" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <BodyText variant="ui" className="text-sm">
            Delete {pendingDelete?.legalName}? The record is marked deleted and leaves this list. The service refuses
            the delete while an active filing names this {noun.singular.toLowerCase()}.
          </BodyText>
          {deleteMutation.error && (
            <Alert type="error" title="The delete did not complete">
              {deleteMutation.error.message}
            </Alert>
          )}
        </div>
      </Dialog>
    </div>
  );
}
